import { ListingCardData } from 'wds/listingCard';
import { TileProps } from 'wds/listingTile';
import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { determinePageType, getQueryStringValue } from '~/modules/analytics/gtmEventTypes/gtmEventTypeHelper';
import { GATileContentData, GATileTagData } from '~/modules/analytics/gtmEventTypes/gtmTileClickType';
import { isSponsoredSection } from '../../services/analyticsService';
import { GA4TileViewEvent, GTMBaseEventWithTiles, GtmEventType } from './gtmEventType';
import { getAppType, getListingAppCost } from './gtmEventTypeHelper';

interface GA4EventCustomProperties {
    listing_name: string;
    listing_type: string;
    listing_id: string;
    listing_url: string;
    element_title: string;
    app_cost?: string;
    app_type?: string;
    internal_source: string;
}

export type GATileViewListingData = ListingCardData | TileProps;

export type GATileViewEvent = {
    listing?: GATileViewListingData;
    keyword: string;
    content?: GATileContentData;
    tag?: GATileTagData;
} & GTMBaseEventWithTiles;

export class GtmTileViewType extends GtmEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_SHOWN_IMPRESSION &&
            (event.elementType === 'listingTile' || event.elementType === 'contentTile')
        );
    }

    getBody(event: GATileViewEvent): GA4TileViewEvent {
        const pageType = determinePageType(window.location.href);
        return {
            event: 'custEv_contentImpression',
            element_type: 'tile',
            lp_detail_interaction: pageType === 'Listing' && event.tiles?.name === 'recommended',
            featured: isSponsoredSection(event.tiles?.name),
            recommended: false,
            ignore_hover: true,
            internal_article: false,
            hover_intent: false,
            page_type: pageType,
            ...this.getGA4EventStructure(event),
        };
    }
    // @ts-expect-error * Automated Type Error Fix
    getListingTileTitle(event) {
        return event.keyword || event.tiles?.title;
    }

    getGA4ListingTileEvent(event: GATileViewEvent): GA4EventCustomProperties {
        const type = (event.listing as TileProps).listingType || (event.listing as ListingCardData).type;
        return {
            listing_name: event.listing?.title || '',
            listing_id: event.listing?.oafId || '',
            listing_type: type || '',
            listing_url: event.listing?.listingUrl || '',
            element_title: this.getListingTileTitle(event),
            app_cost: getListingAppCost(event.listing?.pricing, event.listing?.oafId, type),
            app_type: getAppType(type),
            internal_source: getQueryStringValue(event.listing?.listingUrl || '', 'channel'),
        };
    }

    getGA4ContentTileEvent(event: GATileViewEvent): GA4EventCustomProperties {
        return {
            listing_name: event.content?.title || '',
            listing_id: '',
            listing_type: event.content?.listingType || '',
            listing_url: event.content?.callToAction?.url || '',
            element_title: event.content?.title || '',
            internal_source: getQueryStringValue(event.content?.callToAction?.url || '', 'channel'),
        };
    }

    getGA4EmptyEventCustomProperties(): GA4EventCustomProperties {
        return {
            listing_name: '',
            listing_url: '',
            listing_type: '',
            listing_id: '',
            element_title: '',
            internal_source: '',
        };
    }

    getGA4EventStructure(event: GATileViewEvent): GA4EventCustomProperties {
        if (event.listing) {
            return this.getGA4ListingTileEvent(event);
        }
        if (event.content) {
            return this.getGA4ContentTileEvent(event);
        }
        return this.getGA4EmptyEventCustomProperties();
    }
}
